// "New event" — create an appointment for a lead (shared by the Lead Detail page and the Lead Drawer).
// Sections: Event details · Schedule · Attach to lead · Notifications. Nothing is sent unless its switch is on.
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, CalendarDays, Clock, User, Bell, PlusCircle, Phone, Mail, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet } from '../lib/api.js';
import { fmtPhone } from '../lib/format.js';
import { useAuth } from '../store/auth.js';
import {
  TIMEZONES, REMINDER_OFFSETS, tzLabel, initialForm, eventProblem, buildEvent, zonedToUtc, allNotificationsOn, setAllNotifications,
} from '../lib/appointmentForm.js';
import Toggle from './appointment/Toggle.jsx';
import { NotificationRow, MessageBox, inputClass } from './appointment/NotificationRow.jsx';

const label = 'mb-1 block text-[11px] text-white/50';
const initials = (l) => `${(l?.firstName || '?')[0]}${(l?.lastName || '')[0] || ''}`.toUpperCase();

/**
 * @param {{lead: object, onCancel: () => void, onSubmit: (event: object) => Promise<void>}} props
 *   `onSubmit` receives the event from appointmentForm.buildEvent (title, startAt/endAt in UTC, timezone, busy,
 *   syncedTo, notes, disposition choice and the notifications to send).
 */
export default function ScheduleAppointmentModal({ lead, onCancel, onSubmit }) {
  const agent = useAuth((s) => s.agent);
  const browserTz = typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '';
  const [f, setF] = useState(() => initialForm(lead, { agentTz: agent?.timezone, browserTz }));
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const setNested = (key, patch) => setF((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

  const { data: fb } = useQuery({ queryKey: ['freebusy', f.date], queryFn: () => apiGet(`/calendar/freebusy?date=${f.date}`), enabled: !!f.date, staleTime: 30_000 });
  const { data: dispData } = useQuery({ queryKey: ['dispositions'], queryFn: () => apiGet('/dispositions').catch(() => null), staleTime: 60_000 });
  const googleConnected = !!fb?.googleConnected;
  const actions = (dispData?.dispositions || []).filter((d) => d.systemCode !== 'BOOKED_APPOINTMENT');

  const problem = eventProblem(f, lead);
  const start = zonedToUtc(f.date, f.startTime, f.timezone);
  const end = zonedToUtc(f.date, f.endTime, f.timezone);
  const conflict = f.markBusy && !problem
    ? (fb?.busy || []).find((b) => start.getTime() < new Date(b.endAt).getTime() && end.getTime() > new Date(b.startAt).getTime())
    : null;

  async function submit() {
    if (problem) { toast.error(problem); return; }
    if (conflict && !window.confirm(`Heads up — this overlaps with "${conflict.title}" on your calendar. Create it anyway?`)) return;
    setBusy(true);
    try { await onSubmit(buildEvent(f, lead)); } finally { setBusy(false); }
  }

  const noPhone = !lead?.phone ? 'This lead has no phone number.' : '';
  const noEmail = !lead?.email ? 'This lead has no email address.' : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }} data-testid="new-event-modal" role="dialog" aria-modal="true" aria-label="New event">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-xl border border-civix-border bg-civix-panel shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-civix-border px-6 py-4">
          <h2 className="text-lg font-semibold">New event</h2>
          <button type="button" onClick={onCancel} aria-label="Close" className="text-white/50 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {/* Event details */}
          <section>
            <h3 className="mb-3 flex items-center gap-2 text-base font-medium"><CalendarDays className="h-4 w-4 text-white/50" /> Event details</h3>
            <label className="block"><span className={label}>Event title</span>
              <input value={f.title} onChange={(e) => set({ title: e.target.value })} className={inputClass} data-testid="event-title" />
            </label>
            {f.showDescription ? (
              <label className="mt-3 block"><span className={label}>Description</span>
                <textarea value={f.description} onChange={(e) => set({ description: e.target.value })} rows={3} className={inputClass} data-testid="event-description" />
              </label>
            ) : (
              <button type="button" onClick={() => set({ showDescription: true })} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-civix-purpleSoft hover:text-white" data-testid="add-description">
                <PlusCircle className="h-4 w-4" /> Description
              </button>
            )}
          </section>

          {/* Schedule */}
          <section>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h3 className="flex items-center gap-2 text-base font-medium"><Clock className="h-4 w-4 text-white/50" /> Schedule</h3>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-white/80" data-testid="event-kind">Single day</span>
                <select value={f.timezone} onChange={(e) => set({ timezone: e.target.value })} aria-label="Time zone" className="rounded-md bg-transparent text-white focus:outline-hidden" data-testid="event-timezone">
                  {TIMEZONES.map((z) => <option key={z.value} value={z.value} className="text-black">{tzLabel(z.value, start.getTime() ? start : new Date())}</option>)}
                </select>
                <select value={f.calendar} onChange={(e) => set({ calendar: e.target.value })} aria-label="Calendar" className="rounded-md bg-transparent text-white focus:outline-hidden" data-testid="event-calendar">
                  <option value="civix" className="text-black">CIVIX Calendar</option>
                  {googleConnected && <option value="google" className="text-black">Google Calendar{fb?.googleEmail ? ` (${fb.googleEmail})` : ''}</option>}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 items-end gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
              <label><span className={label}>Start date *</span>
                <input type="date" value={f.date} onChange={(e) => set({ date: e.target.value })} className={inputClass} data-testid="event-date" />
              </label>
              <label><span className={label}>Start time *</span>
                <input type="time" value={f.startTime} onChange={(e) => set({ startTime: e.target.value })} className={inputClass} data-testid="event-start" />
              </label>
              <label><span className={label}>End time *</span>
                <input type="time" value={f.endTime} onChange={(e) => set({ endTime: e.target.value })} className={inputClass} data-testid="event-end" />
              </label>
              <label className="flex items-center gap-2 pb-2.5 text-sm text-white/80">
                <input type="checkbox" checked={f.markBusy} onChange={(e) => set({ markBusy: e.target.checked })} className="accent-civix-purple" data-testid="event-busy" /> Mark as busy
              </label>
            </div>
            {conflict && (
              <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" role="status" data-testid="event-conflict">
                Conflict on your calendar: “{conflict.title}”. You'll be asked to confirm.
              </p>
            )}
          </section>

          {/* Attach to lead */}
          <section>
            <h3 className="mb-3 flex items-center gap-2 text-base font-medium"><User className="h-4 w-4 text-white/50" /> Attach to lead</h3>
            {f.attached ? (
              <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-4" data-testid="attached-lead">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-civix-purple text-sm font-bold">{initials(lead)}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{[lead?.firstName, lead?.lastName].filter(Boolean).join(' ')}</div>
                  <div className="flex flex-wrap items-center gap-x-3 text-xs text-white/60">
                    {lead?.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{fmtPhone(lead.phone).replace(/^\+1\s?/, '')}</span>}
                    {lead?.email && <span className="inline-flex min-w-0 items-center gap-1"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{lead.email}</span></span>}
                  </div>
                </div>
                <button type="button" onClick={() => set({ attached: false })} aria-label="Remove lead" className="text-white/50 hover:text-white" data-testid="detach-lead"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <button type="button" onClick={() => set({ attached: true })} className="text-sm text-civix-purpleSoft underline" data-testid="reattach-lead">
                Attach {[lead?.firstName, lead?.lastName].filter(Boolean).join(' ') || 'this lead'}
              </button>
            )}
          </section>

          {/* Notifications */}
          <section>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-base font-medium"><Bell className="h-4 w-4 text-white/50" /> Notifications</h3>
              <label className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white/60">
                All notifications
                <Toggle checked={allNotificationsOn(f, lead)} onChange={(v) => setF((p) => setAllNotifications(p, lead, v))} label="All notifications" testId="toggle-all" />
              </label>
            </div>

            <NotificationRow title="Drip action" help="Triggered immediately after booking." on={f.actionOn} onToggle={(v) => set({ actionOn: v })} testId="action">
              <select value={f.dispositionId} onChange={(e) => set({ dispositionId: e.target.value })} className={inputClass} aria-label="Action" data-testid="event-action">
                <option value="" className="text-black">Scheduled Appointment</option>
                {actions.map((d) => <option key={d._id} value={d._id} className="text-black">{d.label}</option>)}
              </select>
            </NotificationRow>

            <NotificationRow title="Confirmation text" help="Sent immediately after booking." on={f.confirmationText.on} onToggle={(v) => setNested('confirmationText', { on: v })} disabledReason={noPhone} testId="confirmation-text">
              <MessageBox value={f.confirmationText.body} onChange={(v) => setNested('confirmationText', { body: v })} testId="confirmation-text-body" />
            </NotificationRow>

            <NotificationRow title="Reminder text" help="Sent before the event." on={f.reminderText.on} onToggle={(v) => setNested('reminderText', { on: v })} disabledReason={noPhone} testId="reminder-text">
              <label className="flex items-center gap-2 text-xs text-white/60">Send
                <select value={f.reminderText.beforeMinutes} onChange={(e) => setNested('reminderText', { beforeMinutes: Number(e.target.value) })} className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-white" data-testid="reminder-offset">
                  {REMINDER_OFFSETS.map((o) => <option key={o.value} value={o.value} className="text-black">{o.label}</option>)}
                </select>
                before the event
              </label>
              <MessageBox value={f.reminderText.body} onChange={(v) => setNested('reminderText', { body: v })} testId="reminder-text-body" />
            </NotificationRow>

            <NotificationRow title="Confirmation email" help="Sent immediately after booking." on={f.confirmationEmail.on} onToggle={(v) => setNested('confirmationEmail', { on: v })} disabledReason={noEmail} testId="confirmation-email">
              <input value={f.confirmationEmail.subject} onChange={(e) => setNested('confirmationEmail', { subject: e.target.value })} className={inputClass} aria-label="Email subject" data-testid="confirmation-email-subject" />
              <MessageBox value={f.confirmationEmail.body} onChange={(v) => setNested('confirmationEmail', { body: v })} rows={6} testId="confirmation-email-body" />
            </NotificationRow>
          </section>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-civix-border px-6 py-4">
          {problem && <span className="mr-auto text-xs text-white/45" data-testid="event-problem">{problem}</span>}
          <button type="button" onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
          <button type="button" onClick={submit} disabled={busy || !!problem} data-testid="create-event" className="btn-primary flex items-center gap-2 text-sm font-bold uppercase tracking-wide disabled:opacity-40">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</> : 'Create event'}
          </button>
        </div>
      </div>
    </div>
  );
}
